/**
 * Minimal task helpers for dashboard (assignees, approvals, due labels).
 * Mirrors TasksPage semantics without importing the full page.
 */
import { parseLocalDate, toLocalDateKey, todayKey, offsetDayKey } from './dateUtils'

export type TaskApproval = { userId: string; status: 'pending' | 'approved' | 'denied' }
export type TaskApprovalMode = 'all' | 'one'

export interface DashboardTask {
  id: string
  title: string
  due_date?: string
  status: string
  /** Multi-artist digest: source account for filtering. */
  digestContextArtistId?: string
  assigned_to_user_id?: string
  assigneeId?: string
  assigned_to_user_ids?: string[]
  assigneeIds?: string[]
  metadata?: {
    assignedToIds?: string[]
    assignedTo?: string
    approvals?: TaskApproval[]
    requireApprovals?: boolean
    approvalMode?: TaskApprovalMode
    completedAt?: string | null
  } & Record<string, unknown>
}

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)))
}

export function getTaskAssigneeIds(t: DashboardTask): string[] {
  const fromArray =
    (Array.isArray(t.assigned_to_user_ids) ? t.assigned_to_user_ids : undefined) ??
    (Array.isArray(t.assigneeIds) ? t.assigneeIds : undefined) ??
    (Array.isArray(t.metadata?.assignedToIds) ? t.metadata?.assignedToIds : undefined) ??
    []
  const fromSingle = [t.assigned_to_user_id, t.assigneeId, (t as any).userId, t.metadata?.assignedTo].filter(Boolean) as string[]
  const allIds = uniq([...fromArray, ...fromSingle])
  return allIds.map((id) =>
    id != null && typeof id === 'object' && (id as any).toString ? (id as any).toString() : String(id),
  )
}

export function getTaskApprovals(t: DashboardTask): TaskApproval[] {
  if (t.metadata?.requireApprovals === false) return []
  const approvals = t.metadata?.approvals
  if (Array.isArray(approvals) && approvals.length > 0) {
    return approvals.map((a) => ({
      ...a,
      userId: a.userId != null && typeof a.userId === 'object' && (a.userId as any).toString ? (a.userId as any).toString() : String(a.userId),
    }))
  }
  const assignees = getTaskAssigneeIds(t)
  if (assignees.length === 0) return []
  return assignees.map((userId) => ({ userId, status: 'pending' as const }))
}

export function getApprovalMode(t: DashboardTask): TaskApprovalMode {
  return (t.metadata as any)?.approvalMode === 'one' ? 'one' : 'all'
}

export function isTaskFullyApproved(t: DashboardTask): boolean {
  const approvals = getTaskApprovals(t)
  if (approvals.length === 0) return false
  const mode = getApprovalMode(t)
  if (mode === 'one') return approvals.some((a) => a.status === 'approved')
  return approvals.every((a) => a.status === 'approved')
}

export function isTaskCompleted(t: DashboardTask): boolean {
  const status = (t.status || '').toLowerCase()
  return status === 'completed' || status === 'complete' || status === 'done' || !!t.metadata?.completedAt
}

/** Task is waiting on approval workflow before it can be considered cleared. */
export function taskNeedsApproval(t: DashboardTask): boolean {
  if (t.metadata?.requireApprovals === false) return false
  const approvals = getTaskApprovals(t)
  if (approvals.length === 0) return false
  return !isTaskFullyApproved(t)
}

export function normalizeDashboardTasksFromPayload(raw: any): DashboardTask[] {
  const list = raw?.data?.data ?? raw?.data ?? raw
  const arr = Array.isArray(list) ? list : []
  return arr.map((t: any) => {
    const assignedToIds = t.assigned_to_user_ids ?? t.assigneeIds ?? t.metadata?.assignedToIds
    return {
      ...t,
      id: String(t._id ?? t.id),
      digestContextArtistId:
        t.digestContextArtistId != null ? String(t.digestContextArtistId) : undefined,
      title: t.title || 'Untitled',
      due_date: t.due_date ?? (t.dueDate ? new Date(t.dueDate).toISOString().split('T')[0] : undefined),
      status: t.status || 'pending',
      assigned_to_user_id: t.assigned_to_user_id ?? t.userId ?? t.metadata?.assignedTo,
      assigneeId: t.assigneeId ?? t.userId ?? t.metadata?.assignedTo,
      assigned_to_user_ids: Array.isArray(assignedToIds) ? assignedToIds : undefined,
      assigneeIds: Array.isArray(t.assigneeIds) ? t.assigneeIds : undefined,
      metadata: t.metadata && typeof t.metadata === 'object' ? t.metadata : undefined,
    }
  })
}

/** Due date within the next 7 calendar days (today through today + 6). */
export function taskDueInNextWeek(dueDate: string | undefined): boolean {
  if (!dueDate) return false
  const dk = toLocalDateKey(parseLocalDate(dueDate) || new Date(0))
  const t = todayKey()
  const end = offsetDayKey(6)
  return dk >= t && dk <= end
}

/** Overdue (due before today) or due within the next 7 calendar days (today … today + 6). */
export function taskDueInNextWeekOrOverdue(dueDate: string | undefined): boolean {
  if (!dueDate) return false
  const parsed = parseLocalDate(dueDate)
  if (!parsed) return false
  const dk = toLocalDateKey(parsed)
  const t = todayKey()
  const end = offsetDayKey(6)
  if (dk < t) return true
  return dk >= t && dk <= end
}

export function formatDuePhrase(dueDate: string | undefined): string {
  if (!dueDate) return ''
  const d = parseLocalDate(dueDate)
  if (!d) return ''
  const dk = toLocalDateKey(d)
  const t = todayKey()
  const tom = offsetDayKey(1)
  if (dk < t) return 'Overdue'
  if (dk === t) return 'Due today'
  if (dk === tom) return 'Due tomorrow'
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' })
  return `Due ${weekday}`
}
